import React, { useMemo } from "react";
import { AbsoluteFill, staticFile, Audio, useVideoConfig } from "remotion";
import { linearTiming, TransitionSeries } from "@remotion/transitions";
import { slide } from "@remotion/transitions/slide";
import { z } from "zod";
import { RibbonBadge }  from "../Overlay/RibbonBadge";
import { ImageSlideshow } from "../Compostion/ImageSlideshow";
import { DynamicImageGrid } from "../Compostion/DynamicImageGrid";

/* ── Media item schema (shared shape) ─────────────────────── */
const MediaItemSchema = z.object({
  src: z.string().describe("URL or staticFile path for a video or image"),
  type: z.enum(["video", "image"]).optional().describe("Explicit media type; auto-detected from extension if omitted"),
  crop: z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    sourceWidth: z.number().optional(),
    sourceHeight: z.number().optional(),
  }).nullable().optional().describe("Optional crop rect"),
  videoStartFrom: z.number().default(0).describe("For video: start playback from this frame"),
  videoEndAt: z.number().optional().describe("For video: stop playback at this frame"),
});

// Zod schema for Hair component props
const HairPropsSchema = z.object({
  title: z.string().default("Premium Hair Collection").describe("Main title for the ribbon badge"),
  audioSrc: z.string().optional().describe("Optional audio file path (local or remote MP3)"),
  audioDurationOverride: z.number().default(50).describe("Audio duration in seconds - set this to match your audio file length"),
  imageSlides: z.array(MediaItemSchema).default([
    { src: "wigs/1/hair01.jpg" },
    { src: "wigs/1/hair02.jpg" },
    { src: "wigs/1/hair03.jpg" },
    { src: "wigs/1/hair04.jpg" },
    { src: "wigs/1/hair06.jpg" },
    { src: "wigs/1/hair07.png" },
    { src: "wigs/1/hair08.png" },
  ]).describe("Array of media items for slideshow"),
  images: z.array(MediaItemSchema).default([
    { src: "wigs/1/hair01.jpg" },
    { src: "wigs/1/hair02.jpg" },
    { src: "wigs/1/hair03.jpg" },
    { src: "wigs/1/hair04.jpg" },
    { src: "wigs/1/hair06.jpg" },
    { src: "wigs/1/hair07.png" },
    { src: "wigs/1/hair08.png" },
  ]).describe("Array of media items for slideshow and grid"),
  subtitle: z.string().default("Professional Quality Wigs").describe("Subtitle for the ribbon badge"),
  transitionDuration: z.number().default(100).describe("Duration of transitions between sequences"),
  includeGrid: z.boolean().default(true).describe("Whether to include the dynamic grid sequence"),
  gridAnimationSeed: z.number().default(42).describe("Seed for grid animation randomization"),
  finalShuffleCount: z.number().default(3).describe("Number of final shuffles (lower = smoother)"),
  finalShuffleSpeed: z.number().default(6).describe("Speed of final shuffles (higher = smoother)"),
  audioVolume: z.number().min(0).max(1).default(0.8).describe("Audio volume (0-1)"),
  audioStartFrom: z.number().default(0).describe("Start time in seconds for audio playback"),
});

export { HairPropsSchema };


const Hair = ({
  images = [
    { src: "wigs/1/hair01.jpg" },
    { src: "wigs/1/hair02.jpg" },
    { src: "wigs/1/hair03.jpg" },
    { src: "wigs/1/hair04.jpg" },
    { src: "wigs/1/hair06.jpg" },
    { src: "wigs/1/hair07.png" },
    { src: "wigs/1/hair08.png" },
  ],
  imageSlides = [
    { src: "wigs/1/hair01.jpg" },
    { src: "wigs/1/hair02.jpg" },
    { src: "wigs/1/hair03.jpg" },
    { src: "wigs/1/hair04.jpg" },
    { src: "wigs/1/hair06.jpg" },
    { src: "wigs/1/hair07.png" },
    { src: "wigs/1/hair08.png" },
  ],
  title = "Premium Hair Collection",
  subtitle = "Professional Quality Wigs",
  transitionDuration = 100,
  includeGrid = true,
  gridAnimationSeed = 42,
  finalShuffleCount = 3,
  finalShuffleSpeed = 6,
  audioSrc = undefined,
  audioVolume = 0.8,
  audioStartFrom = 0,
  audioDurationOverride = 50,
}) => {
  const { durationInFrames } = useVideoConfig();

  // Helper function to determine if audio source is remote
  const isRemoteAudio = (src) => {
    return src && (src.startsWith('http://') || src.startsWith('https://'));
  };

  // Safe audio source getter
  const getAudioSrc = (src) => {
    try {
      return isRemoteAudio(src) ? src : staticFile(src);
    } catch (error) {
      console.warn('Failed to load audio source:', src, error);
      return null;
    }
  };

  const audioSource = audioSrc ? getAudioSrc(audioSrc) : null;

  // Calculate adjusted durations based on total video duration
  const totalSequenceDuration = useMemo(() => {
    console.log('Total durationInFrames from audio:', durationInFrames);
    
    const baseTransitionDuration = includeGrid ? transitionDuration : 0;
    const totalAvailable = durationInFrames - baseTransitionDuration;
    
    console.log('Base transition duration:', baseTransitionDuration);
    console.log('Total available frames after transitions:', totalAvailable);
    
    if (includeGrid) {
      // Split time between slideshow and grid
      const adjustedSlideshowDuration = Math.floor(totalAvailable * 0.4); // 40% for slideshow
      const adjustedGridDuration = totalAvailable - adjustedSlideshowDuration; // 60% for grid
      
      const result = {
        slideshowDuration: Math.max(90, adjustedSlideshowDuration), // Minimum 3 seconds
        gridDuration: Math.max(90, adjustedGridDuration), // Minimum 3 seconds
        transitionDuration: baseTransitionDuration
      };
      
      console.log('Calculated durations (with grid):', result);
      return result;
    } else {
      // Use all time for slideshow
      const result = {
        slideshowDuration: Math.max(90, totalAvailable),
        gridDuration: 0,
        transitionDuration: 0
      };
      
      console.log('Calculated durations (slideshow only):', result);
      return result;
    }
  }, [durationInFrames, includeGrid, transitionDuration]);

  return (
    <AbsoluteFill>
      {/* Audio layer - plays throughout entire composition */}
      {audioSource && (
        <Audio
          src={audioSource}
          volume={audioVolume}
          trimBefore={audioStartFrom * 30} // Convert seconds to frames (assuming 30fps)
          // Add error handling for remote audio
          onError={(error) => {
            console.warn('Audio failed to load:', error);
          }}
        />
      )}

      <TransitionSeries>
      {/* First sequence: ImageSlideshow */}
      <TransitionSeries.Sequence durationInFrames={totalSequenceDuration.slideshowDuration + (includeGrid ? totalSequenceDuration.transitionDuration + 30 : 0)}>
        <ImageSlideshow
          images={imageSlides.slice(0, 5)}
          transitionDuration={30}
          enableTransitions={true}
          panIntensity={0.9}
          zoomLevel={1.1}
          visibleTime={Math.max(30, Math.floor((totalSequenceDuration.slideshowDuration - 90) / 3))} // Ensure minimum 30 frames per image
        />

        {/* PopUpText overlay */}
        <RibbonBadge
          title={title}
          subtitle={subtitle}
          y={700}
        />
      </TransitionSeries.Sequence>

      {includeGrid && totalSequenceDuration.gridDuration > 0 && (
        <>
          {/* Transition to Grid */}
          <TransitionSeries.Transition
            presentation={slide({ direction: "from-left" })}
            timing={linearTiming({ durationInFrames: totalSequenceDuration.transitionDuration })}
          />

          {/* Second sequence: DynamicImageGrid */}
          <TransitionSeries.Sequence durationInFrames={totalSequenceDuration.gridDuration}>
            <DynamicImageGrid
              images={images}
              durationInFrames={totalSequenceDuration.gridDuration}
              revealDuration={40}
              hideDuration={30}
              finalRevealDuration={120}
              gridPadding={25}
              backgroundColor="#fff"
              borderRadius={8}
              animationSeed={gridAnimationSeed}
              finalShuffleCount={finalShuffleCount}
              finalShuffleSpeed={finalShuffleSpeed}
            />
          </TransitionSeries.Sequence>
        </>
      )}
    </TransitionSeries>
    </AbsoluteFill>
  );
};

// Calculate metadata function to set duration based on audio length
export const calculateHairMetadata = async ({ props }) => {
  const { audioStartFrom = 0, audioDurationOverride = 50 } = props;
  
  console.log(`🎵 Using audio duration: ${audioDurationOverride}s`);
  
  // Convert to frames (30fps) and subtract start offset
  const effectiveAudioDuration = Math.max(0, audioDurationOverride - audioStartFrom);
  const durationInFrames = Math.ceil(effectiveAudioDuration * 30);
  
  // Minimum duration of 3 seconds (90 frames)
  const finalDuration = Math.max(90, durationInFrames);
  
  console.log(`🎬 Final video duration: ${finalDuration} frames (${Math.round(finalDuration/30)}s)`);

  return {
    durationInFrames: finalDuration,
  };
};

export default Hair;
;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
